import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState
} from "react";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Skeleton from "@material-ui/lab/Skeleton";
import AddIcon from "@material-ui/icons/Add";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ForbiddenPage from "../../components/ForbiddenPage";
import TaskModal from "../../components/TaskModal";
import TaskDetailsModal from "../../components/TaskDetailsModal";
import { TaskStatusChip, TaskPriorityChip, TaskTypeChip } from "../../components/TaskChips";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { getContactNameDisplay } from "../../utils/contactPhoneDisplay";
import moment from "moment";

import useSocketListener from "../../hooks/useSocketListener";
import TaskFilters from "./TaskFilters";
import TaskCard from "./TaskCard";
import TaskSummaryCards from "./TaskSummaryCards";
import {
  buildTaskFilterParams,
  buildDefaultTaskDueDateRange,
  canAccessTaskControl,
  canCreateTask,
  mergeTasksById,
  hasActiveTaskFilters
} from "./taskUtils";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  },
  centeredBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "60vh"
  },
  cardsContainer: {
    width: "100%",
    boxSizing: "border-box"
  },
  clickableRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2
    }
  }
}));

const INITIAL_FILTERS = {
  status: "",
  priority: "",
  type: "",
  assignedUserId: null,
  assignedUserOption: null,
  contactId: null,
  contactOption: null,
  ...buildDefaultTaskDueDateRange()
};

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_TASKS":
      return mergeTasksById(state, action.payload);
    case "REMOVE_TASK":
      return state.filter(task => task.id !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const Tasks = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { user, socket } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  // --- Controle de plano/permissão: aguarda a consulta do plano antes de
  // decidir entre a página e o ForbiddenPage, evitando um "flash" de
  // acesso negado antes da resposta chegar. O backend continua sendo a
  // autoridade final — isto é só controle de exibição. ---
  const [planLoading, setPlanLoading] = useState(true);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const planConfigs = await getPlanCompany(undefined, user.companyId);
        if (active) setPlan(planConfigs.plan);
      } catch (err) {
        if (active) toastError(err);
      } finally {
        if (active) setPlanLoading(false);
      }
    })();

    return () => {
      active = false;
    };
    // getPlanCompany é recriada a cada render pelo hook usePlans — incluí-la
    // aqui causaria chamadas repetidas em loop. Só companyId deve disparar
    // uma nova consulta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.companyId]);

  const hasModuleAccess = canAccessTaskControl({ plan, user });
  const canCreate = canCreateTask({ user });

  // --- Listagem ---
  const [tasks, dispatch] = useReducer(reducer, []);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [modalOpen, setModalOpen] = useState(false);

  const isMountedRef = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Busca uma página específica. Protegida contra resposta fora de ordem
  // via identificador sequencial de requisição (requestIdRef) — se uma
  // busca mais nova já foi disparada quando esta responder, o resultado é
  // descartado, evitando misturar resultados de filtros/páginas diferentes.
  const fetchTasks = useCallback(
    targetPage => {
      const currentRequestId = ++requestIdRef.current;
      setLoading(true);

      (async () => {
        try {
          const params = buildTaskFilterParams({
            searchParam,
            status: filters.status,
            priority: filters.priority,
            type: filters.type,
            assignedUserId: filters.assignedUserId,
            contactId: filters.contactId,
            dueDateFrom: filters.dueDateFrom,
            dueDateTo: filters.dueDateTo,
            pageNumber: targetPage,
            excludeFinished: true
          });

          const { data } = await api.get("/tasks", { params });

          if (!isMountedRef.current || currentRequestId !== requestIdRef.current) return;

          dispatch({ type: "LOAD_TASKS", payload: data.tasks });
          setHasMore(data.hasMore);
          setCount(data.count);
        } catch (err) {
          if (!isMountedRef.current || currentRequestId !== requestIdRef.current) return;
          toastError(err);
        } finally {
          if (isMountedRef.current && currentRequestId === requestIdRef.current) {
            setLoading(false);
          }
        }
      })();
    },
    [searchParam, filters]
  );

  // Busca/filtro mudou: reseta lista/página/hasMore imediatamente e busca a
  // página 1 depois de um debounce (evita uma requisição por tecla digitada).
  useEffect(() => {
    // Invalida imediatamente qualquer resposta da consulta anterior. Sem
    // isso, ela ainda poderia preencher a lista durante os 500 ms de
    // debounce da nova busca/filtro.
    requestIdRef.current += 1;
    setLoading(false);

    if (!hasModuleAccess) return undefined;

    dispatch({ type: "RESET" });
    setHasMore(false);
    setPageNumber(1);

    const delayDebounceFn = setTimeout(() => {
      fetchTasks(1);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, filters, hasModuleAccess]);

  const loadMore = () => {
    if (loading || !hasMore) return;
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    fetchTasks(nextPage);
  };

  const handleScroll = e => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleTaskCreated = () => {
    setModalOpen(false);
    dispatch({ type: "RESET" });
    setHasMore(false);
    setPageNumber(1);
    fetchTasks(1);
  };

  // --- Detalhes ---
  const [detailsTaskId, setDetailsTaskId] = useState(null);

  const hasActiveFiltersOrSearch = () => hasActiveTaskFilters(searchParam, filters);

  // Só o backend reproduz corretamente a busca em título/descrição/
  // contato/responsável/ticket — com filtro ou busca ativos, sempre refaz
  // a consulta (página 1) em vez de tentar replicar a lógica de filtro
  // aqui. fetchTasks já protege contra resposta fora de ordem.
  const resetAndRefetch = useCallback(() => {
    dispatch({ type: "RESET" });
    setHasMore(false);
    setPageNumber(1);
    fetchTasks(1);
  }, [fetchTasks]);

  // --- Tempo real ---
  // O evento do socket é só um sinal opaco ({action:"refresh"}), sem
  // taskId nem dados da tarefa — nunca confiamos nele para decidir o que
  // mudou, só para saber que É PRECISO consultar de novo pelos endpoints
  // autenticados. Tratamento centralizado aqui (a página Tasks), nunca em
  // TaskSummaryCards, que só reage a `summaryRefreshToken`.
  const [summaryRefreshToken, setSummaryRefreshToken] = useState(0);
  const realtimeDebounceRef = useRef(null);

  useEffect(() => {
    return () => {
      if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
    };
  }, [searchParam, filters, hasModuleAccess]);

  const handleRealtimeTaskEvent = useCallback(() => {
    if (!hasModuleAccess) return;

    // Vários eventos em sequência (ex: várias ações seguidas por
    // diferentes usuários) só disparam UMA atualização de resumo e UMA
    // atualização da página 1 — nunca uma chamada por evento.
    if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
    realtimeDebounceRef.current = setTimeout(() => {
      setSummaryRefreshToken(t => t + 1);
      // Mesma função já usada quando uma tarefa é alterada com filtro
      // ativo: reseta lista/página/hasMore e busca a página 1 respeitando
      // exatamente os filtros e a busca atuais (nunca GET /tasks/:taskId
      // — o responsável pode ter mudado, a tarefa pode ter saído/entrado
      // nos filtros, ou o usuário pode ter perdido acesso a ela).
      resetAndRefetch();
    }, 500);
  }, [hasModuleAccess, resetAndRefetch]);

  useSocketListener(socket, user, "task", handleRealtimeTaskEvent, [handleRealtimeTaskEvent]);

  // Chamado pelo TaskDetailsModal após qualquer edição/ação bem-sucedida.
  const handleTaskUpdated = updatedTask => {
    if (hasActiveFiltersOrSearch()) {
      resetAndRefetch();
      return;
    }
    dispatch({ type: "LOAD_TASKS", payload: [updatedTask] });
  };

  // Chamado pelo TaskDetailsModal quando a tarefa retorna 404 (sumiu ou
  // ficou inacessível durante a sessão).
  const handleTaskUnavailable = removedTaskId => {
    if (hasActiveFiltersOrSearch()) {
      resetAndRefetch();
      return;
    }
    dispatch({ type: "REMOVE_TASK", payload: removedTaskId });
  };

  const handleOpenDetails = taskId => {
    setDetailsTaskId(taskId);
  };

  const handleRowKeyDown = (e, taskId) => {
    if (e.key === "Enter") {
      handleOpenDetails(taskId);
    } else if (e.key === " " || e.key === "Spacebar") {
      // Evita rolar a página com a barra de espaço quando o foco está na
      // linha/cartão clicável.
      e.preventDefault();
      handleOpenDetails(taskId);
    }
  };

  if (planLoading) {
    return (
      <MainContainer>
        <Box className={classes.centeredBox}>
          <CircularProgress />
        </Box>
      </MainContainer>
    );
  }

  if (!hasModuleAccess) {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      {modalOpen && (
        <TaskModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSuccess={handleTaskCreated}
        />
      )}

      {detailsTaskId && (
        <TaskDetailsModal
          taskId={detailsTaskId}
          open={Boolean(detailsTaskId)}
          onClose={() => setDetailsTaskId(null)}
          onTaskUpdated={handleTaskUpdated}
          onTaskUnavailable={handleTaskUnavailable}
        />
      )}

      <MainHeader>
        <Title>
          {i18n.t("tasks.title")} ({count})
        </Title>
        <MainHeaderButtonsWrapper>
          {canCreate && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              fullWidth={isMobile}
              onClick={() => setModalOpen(true)}
            >
              {i18n.t("tasks.buttons.new")}
            </Button>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <TaskSummaryCards refreshToken={summaryRefreshToken} />

      <TaskFilters
        searchParam={searchParam}
        onSearchChange={setSearchParam}
        filters={filters}
        onFiltersChange={setFilters}
        isMobile={isMobile}
      />

      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        {isMobile ? (
          <Box className={classes.cardsContainer}>
            {tasks.map(task => (
              <TaskCard key={task.id} task={task} onClick={() => handleOpenDetails(task.id)} />
            ))}
            {loading &&
              Array.from({ length: 3 }).map((_, index) => (
                <Skeleton
                  key={`skeleton-${index}`}
                  variant="rect"
                  height={150}
                  style={{ marginBottom: 12, borderRadius: 4 }}
                />
              ))}
            {!loading && tasks.length === 0 && (
              <Box className={classes.centeredBox}>
                <Typography color="textSecondary">{i18n.t("tasks.emptyState")}</Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("tasks.table.title")}</TableCell>
                <TableCell>{i18n.t("tasks.table.type")}</TableCell>
                <TableCell>{i18n.t("tasks.table.status")}</TableCell>
                <TableCell>{i18n.t("tasks.table.priority")}</TableCell>
                <TableCell>{i18n.t("tasks.table.dueDate")}</TableCell>
                <TableCell>{i18n.t("tasks.table.assignedUser")}</TableCell>
                <TableCell>{i18n.t("tasks.table.contact")}</TableCell>
                <TableCell>{i18n.t("tasks.table.ticket")}</TableCell>
                <TableCell>{i18n.t("tasks.table.createdAt")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map(task => (
                <TableRow
                  key={task.id}
                  hover
                  className={classes.clickableRow}
                  onClick={() => handleOpenDetails(task.id)}
                  onKeyDown={e => handleRowKeyDown(e, task.id)}
                  tabIndex={0}
                  role="button"
                  aria-label={`${i18n.t("tasks.details.title")}: ${task.title}`}
                >
                  <TableCell>{task.title}</TableCell>
                  <TableCell>
                    <TaskTypeChip type={task.type} />
                  </TableCell>
                  <TableCell>
                    <TaskStatusChip status={task.status} />
                  </TableCell>
                  <TableCell>
                    <TaskPriorityChip priority={task.priority} />
                  </TableCell>
                  <TableCell>
                    {task.dueDate
                      ? moment(task.dueDate).format("DD/MM/YYYY")
                      : i18n.t("tasks.table.noDueDate")}
                  </TableCell>
                  <TableCell>{task.assignedUser?.name || i18n.t("tasks.table.none")}</TableCell>
                  <TableCell>
                    {task.contact ? getContactNameDisplay(task.contact) : i18n.t("tasks.table.none")}
                  </TableCell>
                  <TableCell>
                    {task.ticket ? `#${task.ticket.uuid?.slice(0, 8) || task.ticket.id}` : i18n.t("tasks.table.none")}
                  </TableCell>
                  <TableCell>{moment(task.createdAt).format("DD/MM/YYYY")}</TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={9} />}
            </TableBody>
          </Table>
        )}

        {!loading && !isMobile && tasks.length === 0 && (
          <Box className={classes.centeredBox}>
            <Typography color="textSecondary">{i18n.t("tasks.emptyState")}</Typography>
          </Box>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Tasks;
